import * as http from "elm-ts/lib/Http"
import * as cmd from "elm-ts/lib/Cmd"
import * as elmTask from "elm-ts/lib/Task"
import * as E from "fp-ts/Either"
import * as T from "fp-ts/Task"
import * as O from "fp-ts/Option"
import * as elm from "elm-ts"
import { flow, pipe } from "fp-ts/function"
import { User } from "firebase/auth"
import { captureEvent } from "@sentry/react"
import { SENTRY_ENABLED } from "./runtimeVariables"
import { Variant } from "@flypengine/myFlyp-lib"

export * from "elm-ts/lib/Http"

// -- RESPONSE --

export type Response<A> = E.Either<http.HttpError, A>

// -- RESULT --

export namespace reqStatus {
  export const _notAttempted = "notAttempted"
  export const _loading = "loading"
  export const _error = "error"

  export type ReqStatus =
    | Variant.Tagged<typeof _notAttempted, void>
    | Variant.Tagged<typeof _loading, void>
    | Variant.Tagged<typeof _error, string>

  export const ReqStatus = Variant.module<ReqStatus>({
    notAttempted: value => Variant.tagged(_notAttempted, value),
    loading: value => Variant.tagged(_loading, value),
    error: value => Variant.tagged(_error, value),
  })

  export const getErrorOption: (reqStatus: ReqStatus) => O.Option<string> =
    Variant.caseOfWithDefault<O.Option<string>>(O.none)({
      [reqStatus._error]: O.some,
    })
}

export const ReqStatus = reqStatus.ReqStatus
export type ReqStatus = reqStatus.ReqStatus

export type Result<A> = E.Either<reqStatus.ReqStatus, A>

export function resultFromResponse<A>(response: Response<A>): Result<A> {
  return pipe(response, E.mapLeft(flow(httpErrorsToString, ReqStatus.error)))
}

export function sendRequest(
  firebaseUser: User
): <A, msg>(
  handleResponse: (response: E.Either<http.HttpError, A>) => msg
) => (request: http.Request<A>) => cmd.Cmd<msg> {
  return handleResponse => request => {
    return pipe(
      () => firebaseUser.getIdToken(true),
      T.map(idToken => ({
        ...request,
        headers: {
          ...request.headers,
          Authorization: `Bearer ${idToken}`,
          "content-type": "application/json",
        },
      })),
      T.chain(http.toTask),
      elmTask.perform(
        E.match(httpError => {
          if (
            SENTRY_ENABLED &&
            httpError._tag != "BadUrl" && // this is just 404 errors
            (httpError._tag != "BadStatus" ||
              String(httpError.response.status.code).match(/^5/))
          ) {
            captureEvent({
              message: httpErrorsToString(httpError),
              level: "error",
              request,
              tags: {
                user_email: firebaseUser.email,
                http_error: httpError._tag,
              },
              extra: httpError,
            })
          }
          return handleResponse(E.left(httpError))
        }, flow(E.of, handleResponse))
      )
    )
  }
}

//TODO: This function is repeated 3 times now, ideally this would be in a frontend-lib shared between all three frontends
function httpErrorsToString(error: http.HttpError): string {
  switch (error._tag) {
    case "BadUrl":
      return `Bad url error: ${error.value}`
    case "Timeout":
      return "Timeout error"
    case "BadStatus":
      return `Status code: ${error.response.status.code} \nbody: ${error.response.body}`
    case "BadPayload":
      return `Bad payload: ${error.value} \nbody: ${error.response.body}`
    case "NetworkError":
      return `Network error: ${error.value}`
  }
}

export const put = <A>(
  url: string,
  body: unknown,
  decoder: elm.decode.Decoder<A>
): http.Request<A> => ({
  method: "PUT",
  url,
  body,
  expect: decoder,
  headers: {
    "content-type": "application/json",
  },
  timeout: O.none,
  withCredentials: false,
})

export const errorFromResult = <A>(
  result: E.Either<http.HttpError, A>
): O.Option<string> =>
  pipe(result, E.mapLeft(httpErrorsToString), E.swap, O.fromEither)
